import { useState } from 'react'

import { useSessionReplay, useSessions } from '../../lib/admin'
import type { ReplayTurn, SessionSummary } from '../../lib/admintypes'
import { BUSINESS_TIMEZONE, shortDateTime } from '../../lib/datetime'
import { money } from '../../lib/format'

import { AdminLayout } from './AdminLayout'

/**
 * Session replay (WS6-12, 04 §9).
 *
 * The question this screen exists to answer is "why did it say that?", asked after the fact and
 * usually by the person who has to explain it to a homeowner. So it shows what actually
 * happened, in order: the turns, the tools each turn called, what those tools returned, which
 * guardrails fired, what each model call cost, and how every render came out.
 *
 * **Read-only, deliberately.** A replay you can edit is a replay nobody can trust as evidence.
 * Staff who need to change a product or a booking have screens for that.
 *
 * The two things worth surfacing above the fold are the two that indicate something went wrong
 * without anybody noticing: a guardrail that fired, and money. A session that cost eight times
 * the median is interesting whether or not anyone complained.
 */
export function AdminSessions() {
  const [guardrailOnly, setGuardrailOnly] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const sessions = useSessions({ guardrailOnly })

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl">Sessions</h1>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={guardrailOnly}
            onChange={(event) => {
              setGuardrailOnly(event.target.checked)
              setOpen(null)
            }}
            className="size-5"
          />
          Only where a guardrail fired
        </label>
      </div>
      <p className="mt-2 max-w-[70ch] text-sm text-muted">
        What the assistant was asked, what it did about it, and what each answer cost. Read-only.
      </p>

      {sessions.isPending && <p className="mt-6 text-sm text-muted">Loading…</p>}
      {sessions.data?.length === 0 && (
        <p className="mt-6 text-sm text-muted">
          {guardrailOnly
            ? 'No guardrail ever fired. That is the good outcome.'
            : 'No sessions yet.'}
        </p>
      )}

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[820px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left font-mono text-xs uppercase tracking-[0.12em] text-muted">
              <th className="py-2 pr-3 font-normal">Who</th>
              <th className="py-2 pr-3 font-normal">Last activity</th>
              <th className="py-2 pr-3 font-normal">Turns</th>
              <th className="py-2 pr-3 font-normal">Renders</th>
              <th className="py-2 pr-3 font-normal">Guardrails</th>
              <th className="py-2 pr-3 text-right font-normal">Cost</th>
              <th className="py-2 font-normal" />
            </tr>
          </thead>
          <tbody>
            {(sessions.data ?? []).map((session) => (
              <Row
                key={session.id}
                session={session}
                open={open === session.id}
                onToggle={() => setOpen(open === session.id ? null : session.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  )
}

function Row({
  session,
  open,
  onToggle,
}: {
  session: SessionSummary
  open: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className="border-b border-line align-top">
        {/* Identity first. Every row used to open "8 turns, 1 render, $0.0477" and nothing
            else, so finding one homeowner's session meant opening the replay on each in turn. */}
        <td className="py-2.5 pr-3">
          <span className="font-semibold text-ink">{session.homeowner ?? 'Unknown'}</span>
          {session.categories.length > 0 && (
            <span className="block text-xs text-muted">{session.categories.join(' · ')}</span>
          )}
        </td>
        <td className="py-2.5 pr-3 tabular-nums">
          {shortDateTime(session.last_activity_at, BUSINESS_TIMEZONE)}
        </td>
        <td className="py-2.5 pr-3 tabular-nums">{session.turns}</td>
        <td className="py-2.5 pr-3 tabular-nums">{session.renders}</td>
        <td className="py-2.5 pr-3 tabular-nums">
          {session.guardrails > 0 ? (
            <span className="rounded-pill bg-sand px-2 py-0.5 text-xs font-semibold text-ink">
              {session.guardrails}
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="py-2.5 pr-3 text-right tabular-nums">{money(Number(session.spent_usd))}</td>
        <td className="py-2.5 whitespace-nowrap">
          {session.estimate_request_id && (
            <a
              href={`/op/requests/${session.estimate_request_id}`}
              className="mr-2 text-sm font-semibold text-blue-deep underline-offset-2 hover:underline"
            >
              Open request
            </a>
          )}
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="relative min-h-11 rounded-pill border border-line px-3.5 text-sm font-semibold text-ink hover:border-blue"
          >
            {open ? 'Hide' : 'Replay'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="pb-6">
            <Replay sessionId={session.id} />
          </td>
        </tr>
      )}
    </>
  )
}

function Replay({ sessionId }: { sessionId: string }) {
  const replay = useSessionReplay(sessionId)

  if (replay.isPending) return <p className="text-sm text-muted">Loading the session…</p>
  if (!replay.data) return <p className="text-sm text-ink">That session could not be loaded.</p>

  const { turns, usage, renders, spent_usd, budget_usd } = replay.data
  const started = turns[0] ? new Date(turns[0].created_at).getTime() : 0

  return (
    <div className="rounded-card border border-line bg-paper p-4">
      <p className="m-0 font-mono text-xs uppercase tracking-[0.12em] text-muted">
        {money(Number(spent_usd))} of {money(Number(budget_usd))} · {usage.length} model calls ·{' '}
        {renders.length} renders
      </p>

      <ol className="m-0 mt-4 grid list-none gap-3 p-0">
        {turns.map((turn) => (
          <Turn key={turn.id} turn={turn} started={started} />
        ))}
      </ol>

      {renders.length > 0 && (
        <>
          <h3 className="mt-6 mb-2 font-mono text-xs uppercase tracking-[0.12em] text-muted">
            Renders
          </h3>
          <ul className="m-0 grid list-none gap-1.5 p-0 text-sm">
            {renders.map((render) => (
              <li key={render.id} className="tabular-nums">
                {render.status} · {render.quality} · {render.model ?? 'no model'} ·{' '}
                {render.latency_ms ? `${(render.latency_ms / 1000).toFixed(1)}s` : '—'} ·{' '}
                {money(Number(render.cost_usd))}
                {render.low_confidence && (
                  <span className="ml-2 rounded-pill bg-sand px-2 py-0.5 text-xs">
                    low confidence
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function Turn({ turn, started }: { turn: ReplayTurn; started: number }) {
  const at = new Date(turn.created_at).getTime()
  const offset = started ? `+${((at - started) / 1000).toFixed(1)}s` : ''
  // `{ ok: true, actions: [] }` is what every clean assistant turn stores. Only a non-empty
  // `actions` list means a rule actually did something (04 §7).
  const fired = Array.isArray(turn.guardrail.actions) ? (turn.guardrail.actions as string[]) : []

  return (
    <li className="rounded-card border border-line bg-white p-3">
      <div className="flex flex-wrap items-baseline gap-2 font-mono text-xs uppercase tracking-[0.12em] text-muted">
        <span className="text-ink">{turn.role === 'user' ? 'Homeowner' : 'Studio'}</span>
        {turn.intent && <span>{turn.intent}</span>}
        <span className="tabular-nums">{offset}</span>
      </div>

      <p className="m-0 mt-1.5 whitespace-pre-wrap text-sm text-body">{turn.content}</p>

      {turn.tool_calls.length > 0 && (
        <ul className="m-0 mt-2.5 grid list-none gap-1 p-0 text-sm">
          {turn.tool_calls.map((call, index) => (
            <li key={index} className="rounded-btn bg-paper px-2.5 py-1.5">
              <span className="font-mono text-xs text-ink">{call.tool}</span>
              <span className="ml-2 text-muted">
                {JSON.stringify(call.input).slice(0, 120)} →{' '}
                {JSON.stringify(call.output_summary).slice(0, 120)}
              </span>
              {call.latency_ms != null && (
                <span className="ml-2 tabular-nums text-muted">{call.latency_ms} ms</span>
              )}
              {call.error && <span className="ml-2 font-semibold text-ink">{call.error}</span>}
            </li>
          ))}
        </ul>
      )}

      {fired.length > 0 && (
        <p className="m-0 mt-2 text-xs text-muted">Guardrails: {fired.join(', ')}</p>
      )}
    </li>
  )
}
